/** @jsxImportSource preact */
import type { ComponentChildren } from "preact";
import { useId } from "preact/hooks";

import * as m from "../../paraglide/messages.js";
import { Checkbox } from "../form/base-ui.mjs";
import { CARD_STYLES } from "../form/card-styles";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { SELECT_FIELD_STYLES, SelectField } from "../form/select-field";
import { TEXT_AREA_FIELD_STYLES, TextAreaField } from "../form/text-area-field";
import { TextField } from "../form/text-field";
import {
  CheckIcon,
  ExternalLinkIcon,
  LockIcon,
  MailIcon,
  PaperPlaneIcon,
  ShieldCheckIcon,
} from "../icons";
import { CARD_PARTS_STYLES, FactRow, Facts } from "./card-parts";
import type { WidgetTokens } from "../theme";

/** 八种固定的请求类型；名字是固定文案，由挂件按枚举给。 */
export type WidgetContactRequestType =
  | "delivery_issues"
  | "order_cancellation"
  | "order_changes"
  | "order_status"
  | "other"
  | "product_issues"
  | "returns"
  | "subscriptions";

/** 邮箱是从哪儿带来的：已核验的结账邮箱，或店铺账号。买家自己填的不写提示。 */
export type WidgetContactEmailSource = "account" | "checkout";

/** 邮箱的三种错误：没填、格式不对、填成了商家或 GoNovus 自己的地址。 */
export type WidgetContactEmailError = "empty" | "format" | "own";

/** 订单号的来历：核验过的订单，或买家自己输入、商家去核对。 */
export type WidgetContactOrderSource = "entered" | "verified";

/** 字段上方「GoNovus 查到了什么」的面板。 */
export interface WidgetContactFound {
  /** 右上角的小徽标文字，如 `From your order`；没有就不画。 */
  badge?: string;
  /** 面板标题，如 `Tracking`、`What GoNovus found`。 */
  heading: string;
  /** 一到三行，原文由后端给。 */
  lines: string[];
}

export interface WidgetContactOrderReference {
  /**
   * 给了才画下面的提示；核验过的订单号另在标签右边画 Verified 徽标，买家自己填的不画徽标（GON-347）。
   * 不给就只是一个可选的输入框。
   */
  source?: WidgetContactOrderSource;
  value: string;
}

/** 一件相关商品。没图用占位块；没链接就不出 View product。 */
export interface WidgetContactProduct {
  /** 勾选状态由外面管：勾上的商品会写进邮件。 */
  checked: boolean;
  /** 规格和数量，如 `Black / M · Qty 1`。 */
  detail: string;
  id: string;
  imageUrl?: string;
  /** 价格和库存或订单状态，如 `USD 149.00 · In stock`。 */
  status: string;
  title: string;
  /** 商品页链接，在新页面打开。 */
  url?: string;
}

/**
 * 采集卡（How can the store help?）的全部数据和回调，平铺成一份 props。
 * 买家能改的字段（类型、邮箱、描述、订单号、商品勾选）都是受控的：值从这里进，
 * 改动通过 on*Change 出去。后端生成的文字原样显示；固定文案（标签、提示、错误、类型名）由枚举挑。
 */
export interface WidgetContactCard {
  /** 描述框是不是从对话里预填的：是就显示 `We filled this in from your chat`。 */
  descriptionPrefilled?: boolean;
  /** 描述为空时的错误。 */
  descriptionError?: boolean;
  description: string;
  fieldErrors?: Partial<
    Record<
      "contact_email" | "description" | "order_reference" | "request_type",
      string
    >
  >;
  draftActions?: ComponentChildren;
  email: string;
  emailError?: WidgetContactEmailError;
  emailSource?: WidgetContactEmailSource;
  /** 上方的「查到了什么」面板；没有就不出。 */
  found?: WidgetContactFound;
  onDescriptionChange?: (value: string) => void;
  onDismiss?: () => void;
  onEmailChange?: (value: string) => void;
  onOrderReferenceChange?: (value: string) => void;
  onProductChange?: (id: string, checked: boolean) => void;
  onRequestTypeChange?: (type: WidgetContactRequestType) => void;
  onReview?: () => void;
  onSend?: () => void;
  /** 有它就出 Order reference；没有就不出。 */
  orderReference?: WidgetContactOrderReference;
  /** 「Price and stock as of …」里的时间，已按买家语言格式化；没有就不出这一行。 */
  productsAsOf?: string;
  /** 一件或多件；没有或为空就不出 Related products。 */
  products?: WidgetContactProduct[];
  /** 商家收信邮箱，只读显示在 To · Store 里。 */
  recipient: string;
  requestType: WidgetContactRequestType;
  /** 顾客具体范围的只读摘要，修改描述或分类时由 owner 清除。 */
  requestScope?: string;
  /** 受控地固定展开请求类型的列表；设计稿的静态展示用。 */
  requestTypeOpen?: boolean;
  tokens: WidgetTokens;
}

export const REQUEST_TYPE_LABELS: Record<
  WidgetContactRequestType,
  () => string
> = {
  order_status: m.widget_contact_type_order_status,
  order_cancellation: m.widget_contact_type_order_cancellation,
  order_changes: m.widget_contact_type_order_changes,
  delivery_issues: m.widget_contact_type_delivery_issues,
  returns: m.widget_contact_type_returns,
  product_issues: m.widget_contact_type_product_issues,
  subscriptions: m.widget_contact_type_subscriptions,
  other: m.widget_contact_type_other,
};

/** 列表里的顺序就是设计稿的顺序。 */
const REQUEST_TYPES = Object.keys(
  REQUEST_TYPE_LABELS,
) as WidgetContactRequestType[];

/** 浏览器自动填充的提示。 */
const EMAIL_AUTOCOMPLETE = "email";

const EMAIL_HINTS: Record<WidgetContactEmailSource, () => string> = {
  checkout: m.widget_contact_email_hint_checkout,
  account: m.widget_contact_email_hint_account,
};

const EMAIL_ERRORS: Record<WidgetContactEmailError, () => string> = {
  empty: m.widget_contact_email_error_empty,
  format: m.widget_contact_email_error_format,
  own: m.widget_contact_email_error_own,
};

/** 请求类型的枚举值收窄：Base UI 回给我们的是字符串。 */
function isRequestType(value: string): value is WidgetContactRequestType {
  return REQUEST_TYPES.some((type) => type === value);
}

/**
 * 采集卡专属的样式（外壳、井、胶囊、小圆点在 CARD_STYLES，输入井在 FORM_STYLES）。
 * 软胶囊皮肤：字段标签和说明缩进 4px 对齐标题；相关商品是井里的白色胶囊行；
 * 收件人和「查到了什么」是内凹井；底部只有一个墨黑实心的 Send request。整封邮件只在 Review request 的预览里看（GON-353）。
 * 核验过的订单号，标签和 Verified 徽标排在同一行：标签占剩下的宽度，放不下就换行，不会钻到徽标底下。
 * 带徽标的标签行（核验过的订单号、「查到了什么」）固定至少 24px 高、标签竖向居中；
 * 下面的纸面离它 10px（卡片分段间距）。普通标签离输入框 6px，加上文字自身的留白看上去约 10px，
 * 实心徽标没有这层留白，也按 6px 摆就会贴着下面的白色输入框（GON-301）。
 */
export const CONTACT_CARD_STYLES = `
.gn-collect-body { gap:14px; }
.gn-collect-body .gn-form-label, .gn-collect-body .gn-form-hint, .gn-collect-body .gn-form-error { padding:0 4px; }
.gn-collect-body .gn-form-hint, .gn-collect-body .gn-form-error { font-size:12px; }
.gn-collect-body .gn-form-error > svg { margin-top:1px; }
.gn-collect-found { display:flex; flex-direction:column; gap:10px; }
.gn-collect-found-head { display:flex; justify-content:space-between; align-items:center; gap:8px; min-height:24px; padding:0 4px; }
.gn-collect-found-head > .gn-form-label { padding:0; }
.gn-collect-found-box { gap:4px; font-size:13px; font-weight:600; overflow-wrap:anywhere; }
.gn-collect-badge { box-sizing:border-box; display:inline-flex; align-items:center; gap:4px; min-height:24px; padding:0 10px; border-radius:var(--gn-form-r-pill); background:var(--gn-form-sheet); color:var(--gn-form-ink); font-size:12px; font-weight:700; white-space:nowrap; }
.gn-collect-ref { display:grid; grid-template-columns:minmax(0, 1fr) auto; grid-template-rows:minmax(24px, auto); align-items:center; gap:6px 8px; }
.gn-collect-ref > .gn-form-field { display:contents; }
.gn-collect-ref .gn-form-field > * { grid-column:1 / -1; }
.gn-collect-ref > .gn-collect-badge { grid-row:1; grid-column:2; margin-right:4px; }
.gn-collect-ref > .gn-collect-badge + .gn-form-field > .gn-form-label { grid-row:1; grid-column:1; }
.gn-collect-ref .gn-form-wrap { margin-top:4px; }
.gn-collect-products { display:flex; flex-direction:column; gap:6px; min-width:0; margin:0; padding:0; border:0; }
.gn-collect-products > legend { margin-bottom:6px; padding:0 4px; }
.gn-collect-product-list { gap:6px; padding:0; background:transparent; }
.gn-collect-product { display:flex; align-items:center; gap:10px; padding:8px 12px 8px 8px; border-radius:var(--gn-form-r-cell); background:var(--gn-form-sheet); }
.gn-collect-product[data-unchecked] { opacity:.6; }
.gn-collect-check { box-sizing:border-box; display:inline-flex; flex:none; align-items:center; justify-content:center; width:22px; height:22px; margin:0; padding:0; border:0; border-radius:var(--gn-form-r-pill); background:var(--gn-form-chip); color:var(--gn-form-answer-fg); cursor:pointer; }
.gn-collect-check[data-checked] { background:var(--gn-form-answer); }
.gn-collect-check:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; }
.gn-collect-thumb { flex:none; display:block; box-sizing:border-box; width:40px; height:40px; border-radius:min(12px, var(--gn-form-r-cell)); object-fit:cover; background:var(--gn-form-chip); }
.gn-collect-product-body { display:flex; flex:1 1 0; flex-direction:column; gap:2px; min-width:0; }
.gn-collect-product-title { font-weight:700; overflow-wrap:anywhere; }
.gn-collect-product-detail { font-size:12px; color:var(--gn-form-quiet); }
.gn-collect-product-status { font-size:12px; font-weight:600; color:var(--gn-form-ink); font-variant-numeric:tabular-nums; }
.gn-collect-link { display:inline-flex; align-items:center; align-self:flex-start; gap:4px; font-size:12px; font-weight:700; color:var(--gn-form-ink); text-decoration:underline; text-underline-offset:2px; }
.gn-collect-link:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:2px; border-radius:var(--gn-form-r-pill); }
.gn-collect-asof { padding:0 4px; font-size:12px; color:var(--gn-form-quiet); }
.gn-collect-recipient { display:flex; flex-direction:column; gap:6px; }
.gn-collect-recipient-box { box-sizing:border-box; display:flex; align-items:center; gap:8px; min-height:44px; padding:0 14px; border-radius:var(--gn-form-r-input); background:var(--gn-form-sheet); color:var(--gn-form-ink); font-size:13px; font-weight:600; overflow-wrap:anywhere; }
.gn-collect-recipient-box > svg { flex:none; color:var(--gn-form-quiet); }
.gn-collect-foot { gap:10px; }
.gn-collect-note { display:flex; align-items:flex-start; gap:6px; padding:0 4px; font-size:12px; color:var(--gn-form-quiet); }
.gn-collect-note > svg { flex:none; margin-top:2px; color:var(--gn-form-faint); }
.gn-collect-actions { display:flex; gap:6px; }
.gn-collect-actions > .gn-btn { flex:1 1 0; width:auto; min-width:0; }
`;

function Badge({ children }: { children: ComponentChildren }) {
  return <span class="gn-collect-badge">{children}</span>;
}

function FoundPanel({ found }: { found: WidgetContactFound }) {
  const headingId = useId();
  return (
    <div aria-labelledby={headingId} class="gn-collect-found" role="group">
      <div class="gn-collect-found-head">
        <span class="gn-form-label" id={headingId}>
          {found.heading}
        </span>
        {found.badge !== undefined && (
          <Badge>
            <ShieldCheckIcon size={12} />
            {found.badge}
          </Badge>
        )}
      </div>
      <div class="gn-well gn-collect-found-box" data-roomy="">
        {found.lines.map((line, index) => (
          <div key={`${String(index)}:${line}`}>{line}</div>
        ))}
      </div>
    </div>
  );
}

function ProductRow({
  onChange,
  product,
}: {
  onChange?: (id: string, checked: boolean) => void;
  product: WidgetContactProduct;
}) {
  const inputId = useId();
  return (
    <div
      class="gn-collect-product"
      data-unchecked={product.checked ? undefined : ""}
    >
      <Checkbox.Root
        checked={product.checked}
        className="gn-collect-check"
        id={inputId}
        onCheckedChange={(checked) => onChange?.(product.id, checked)}
      >
        <Checkbox.Indicator>
          <CheckIcon size={12} strokeWidth={3} />
        </Checkbox.Indicator>
      </Checkbox.Root>
      {product.imageUrl !== undefined && product.imageUrl !== "" ? (
        <img alt="" class="gn-collect-thumb" src={product.imageUrl} />
      ) : (
        <span aria-hidden="true" class="gn-collect-thumb" data-placeholder="" />
      )}
      <div class="gn-collect-product-body">
        <label class="gn-collect-product-title" for={inputId}>
          {product.title}
        </label>
        <div class="gn-collect-product-detail">{product.detail}</div>
        <div class="gn-collect-product-status">{product.status}</div>
        {product.url !== undefined && (
          <a
            class="gn-collect-link"
            href={product.url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {m.widget_contact_product_view()}
            <ExternalLinkIcon size={12} />
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * 采集卡（PRD 2.6，How can the store help?）：让买家把一句话写给商家。
 * 从上到下：可选的「查到了什么」面板、请求类型、联系邮箱、描述、可选的订单号、可选的相关商品、
 * 只读的收件人，底部是回复说明、Send request、Review request 和 Not now。
 * 各字段由 props 控制，每种状态（下拉展开、字段出错）都能直接用 props 画出来。
 */
export function ContactCard(props: WidgetContactCard) {
  const {
    tokens: t,
    description,
    descriptionError = false,
    descriptionPrefilled = false,
    email,
    emailError,
    emailSource,
    found,
    onDescriptionChange,
    onDismiss,
    onEmailChange,
    onOrderReferenceChange,
    onProductChange,
    onRequestTypeChange,
    onReview,
    onSend,
    orderReference,
    products,
    productsAsOf,
    recipient,
    requestType,
    requestTypeOpen,
  } = props;
  const recipientId = useId();
  const orderSource = orderReference?.source;
  // 买家自己填的订单号不画徽标，只留下面那句 Not verified 提示，标签行也不为徽标留高度（GON-347）。
  const orderField = orderReference && (
    <TextField
      description={
        orderSource === "verified"
          ? m.widget_contact_order_hint_verified()
          : orderSource === "entered"
            ? m.widget_contact_order_hint_unverified()
            : undefined
      }
      error={props.fieldErrors?.order_reference}
      label={m.widget_contact_order_label()}
      labelNote={m.widget_contact_optional()}
      onValueChange={onOrderReferenceChange}
      placeholder={m.widget_contact_order_placeholder()}
      tokens={t}
      value={orderReference.value}
    />
  );
  return (
    <div
      aria-label={m.widget_contact_title()}
      class="gn-card"
      role="group"
      style={formVars(t)}
    >
      <style>
        {`${FORM_STYLES}${CARD_STYLES}${SELECT_FIELD_STYLES}${TEXT_AREA_FIELD_STYLES}${CONTACT_CARD_STYLES}${CARD_PARTS_STYLES}`}
      </style>
      <div class="gn-card-head">
        <div class="gn-card-title">{m.widget_contact_title()}</div>
      </div>
      <div class="gn-card-body gn-collect-body">
        {found !== undefined && <FoundPanel found={found} />}
        <SelectField
          label={m.widget_contact_request_type()}
          error={props.fieldErrors?.request_type}
          onValueChange={(value) => {
            if (isRequestType(value)) onRequestTypeChange?.(value);
          }}
          open={requestTypeOpen}
          options={REQUEST_TYPES.map((type) => ({
            label: REQUEST_TYPE_LABELS[type](),
            value: type,
          }))}
          tokens={t}
          value={requestType}
        />
        {props.requestScope && (
          <Facts>
            <FactRow
              label={m.widget_support_scope()}
              value={props.requestScope}
            />
          </Facts>
        )}
        <TextField
          autoComplete={EMAIL_AUTOCOMPLETE}
          description={
            emailSource !== undefined ? EMAIL_HINTS[emailSource]() : undefined
          }
          error={
            props.fieldErrors?.contact_email ??
            (emailError !== undefined ? EMAIL_ERRORS[emailError]() : undefined)
          }
          inputMode="email"
          label={m.widget_contact_email_label()}
          onValueChange={onEmailChange}
          placeholder={m.widget_contact_email_placeholder()}
          tokens={t}
          type="email"
          value={email}
        />
        <TextAreaField
          description={
            descriptionPrefilled
              ? m.widget_contact_description_hint()
              : undefined
          }
          error={
            props.fieldErrors?.description ??
            (descriptionError
              ? m.widget_contact_description_error()
              : undefined)
          }
          label={m.widget_contact_description_label()}
          onValueChange={onDescriptionChange}
          placeholder={m.widget_contact_description_placeholder()}
          tokens={t}
          value={description}
        />
        {products !== undefined && products.length > 0 && (
          <fieldset class="gn-collect-products">
            <legend class="gn-form-label">
              {m.widget_contact_products_legend()}
            </legend>
            <div class="gn-well gn-collect-product-list">
              {products.map((product) => (
                <ProductRow
                  key={product.id}
                  onChange={onProductChange}
                  product={product}
                />
              ))}
            </div>
            {productsAsOf !== undefined && (
              <div class="gn-collect-asof">
                {m.widget_contact_products_as_of({ time: productsAsOf })}
              </div>
            )}
          </fieldset>
        )}
        {orderSource === "verified" ? (
          <div class="gn-collect-ref">
            <Badge>
              <ShieldCheckIcon size={12} />
              {m.widget_contact_order_verified()}
            </Badge>
            {orderField}
          </div>
        ) : (
          orderField
        )}
        <div
          aria-labelledby={recipientId}
          class="gn-collect-recipient"
          role="group"
        >
          <div class="gn-form-label" id={recipientId}>
            {m.widget_contact_recipient_label()}
          </div>
          <div class="gn-collect-recipient-box">
            <LockIcon size={13} />
            <span>{recipient}</span>
          </div>
        </div>
      </div>
      <div class="gn-card-foot gn-collect-foot">
        {props.draftActions ?? (
          <>
            <div class="gn-collect-note">
              <MailIcon size={13} strokeWidth={2} />
              <span>{m.widget_contact_reply_note()}</span>
            </div>
            <FormButton fullWidth onClick={onSend} tokens={t}>
              <PaperPlaneIcon size={14} />
              {m.widget_contact_send()}
            </FormButton>
            <div class="gn-collect-actions">
              <FormButton onClick={onReview} tokens={t} variant="secondary">
                {m.widget_contact_review()}
              </FormButton>
              <FormButton onClick={onDismiss} tokens={t} variant="ghost">
                {m.widget_contact_dismiss()}
              </FormButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
